import { useMemo } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Lock, Info, ArrowRight } from "lucide-react";
import { tierMeta } from "../lib/tiers";
import { maxGain as computeMaxGain, barPercent, isInertLever, unlocksTier } from "../lib/levers";

/**
 * "What would move your score" — the ranked counterfactual levers.
 *
 * Each lever answers one question: if this single signal were perfect and
 * nothing else changed, how many points would the score gain, and would the
 * tier change? The backend computes it as exact arithmetic on the stored
 * attribution (`backend/counterfactual.py`), already sorted by gain.
 *
 * This is deliberately preferred over the model's `improvement_suggestions`.
 * The ordering here comes from the scorecard, so it cannot be wrong about which
 * lever is biggest; a generated list can be, and has no way to know it is.
 *
 * Two things here are features, not oversights:
 *
 *   * Zero-weight levers are rendered, greyed, carrying their note. Showing a
 *     student that growing their following cannot move the number is the point.
 *     Filtering them out would hide exactly the thing worth saying.
 *   * The caveat is printed verbatim. These are ceilings under a
 *     one-signal-at-a-time assumption, not forecasts, and presenting them as
 *     promises would be a claim the product cannot keep.
 */

function Lever({ lever, maxGain, rank }) {
    const gain = Number(lever?.gain_if_perfect) || 0;
    const inert = isInertLever(lever);
    const unlocks = unlocksTier(lever);
    const target = unlocks ? tierMeta(lever.tier_if_perfect) : null;
    const pct = barPercent(gain, maxGain);

    return (
        <li
            className={`list-none p-3 sm:p-4 rounded-xl border transition-colors ${
                unlocks
                    ? "bg-white/[0.04] border-white/15"
                    : inert
                      ? "bg-white/[0.01] border-white/5"
                      : "bg-white/[0.02] border-white/[0.07]"
            }`}
            data-testid={`lever-${lever.signal}`}
            data-inert={inert ? "true" : "false"}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-mono text-[10px] text-text-secondary/50 tabular-nums">
                            {rank}
                        </span>
                        <span
                            className={`text-[11px] uppercase tracking-wider font-semibold truncate ${
                                inert ? "text-text-secondary/45" : "text-text-secondary/75"
                            }`}
                        >
                            {lever.label}
                        </span>
                        {inert && <Lock size={10} className="text-text-secondary/40 shrink-0" />}
                    </div>
                    {/* The action, not the signal name: advice a student can follow. */}
                    <p
                        className={`text-[13px] leading-snug ${
                            inert ? "text-text-secondary/50" : "text-text-primary"
                        }`}
                    >
                        {lever.action || lever.label}
                    </p>
                </div>

                <div className="shrink-0 text-right">
                    {inert ? (
                        <span className="font-mono text-sm text-text-secondary/40">+0</span>
                    ) : (
                        <span className="font-mono text-xl sm:text-2xl font-bold text-text-primary tabular-nums leading-none">
                            +{gain}
                        </span>
                    )}
                </div>
            </div>

            {/* The tier unlock is the emotional point. "+94" alone is a number. */}
            {unlocks && (
                <div
                    className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold ${target.bg} ${target.border} border ${target.text}`}
                    data-testid={`lever-unlock-${lever.signal}`}
                >
                    <ArrowRight size={11} className="shrink-0" />
                    <span>
                        Reaches {lever.score_if_perfect} — unlocks {lever.tier_if_perfect}
                    </span>
                </div>
            )}

            {lever.note && (
                <p className="mt-2 text-[11px] leading-snug text-text-secondary/55">{lever.note}</p>
            )}

            <div className="mt-2.5 h-1.5 rounded-full bg-white/[0.06] overflow-hidden" aria-hidden="true">
                <motion.div
                    className="h-full rounded-full"
                    style={{
                        background: unlocks
                            ? `linear-gradient(90deg, ${target.color}, ${target.color}bb)`
                            : "linear-gradient(90deg, rgba(167,139,250,.75), rgba(34,211,238,.6))",
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.55, delay: 0.05 * rank, ease: "easeOut" }}
                />
            </div>

            <div className="mt-1.5 flex items-center justify-between text-[10px] text-text-secondary/45">
                <span className="tabular-nums">now {lever.current_subscore}/100</span>
                <span className="tabular-nums">{Math.round((Number(lever.weight) || 0) * 100)}% weight</span>
            </div>
        </li>
    );
}

export default function ScoreLevers({ counterfactual }) {
    const levers = useMemo(
        () => (counterfactual?.levers || []).filter(Boolean),
        [counterfactual]
    );

    // Scale to the largest lever, not a fixed divisor: with a fixed one the top
    // few saturate and the ranking the whole panel exists to show disappears.
    const maxGain = useMemo(() => computeMaxGain(levers), [levers]);

    if (!levers.length) return null;

    return (
        <div data-testid="score-levers">
            <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-cyan-signal/15 border border-cyan-signal/25 flex items-center justify-center shrink-0">
                        <TrendingUp size={16} className="text-cyan-signal" />
                    </div>
                    <div>
                        <h3 className="font-heading text-lg font-medium text-text-primary">
                            What would move your score
                        </h3>
                        <p className="text-text-secondary text-xs">
                            Ranked by how much each signal could gain
                        </p>
                    </div>
                </div>
                {counterfactual.scorecard_version && (
                    <span className="font-mono text-[10px] px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 text-text-secondary/60 shrink-0">
                        scorecard {counterfactual.scorecard_version}
                    </span>
                )}
            </div>

            {/* Why this list and not the model's suggestions. Worth one line of
                copy: it is the difference between computed and generated. */}
            <p className="mt-3 mb-4 text-[11px] leading-snug text-text-secondary/60 flex items-start gap-1.5">
                <Info size={12} className="shrink-0 mt-0.5 text-cyan-signal/70" />
                <span>
                    This ordering is computed from the scorecard itself, not written by a
                    model, so it cannot be wrong about which lever is biggest.
                </span>
            </p>

            <ol className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
                {levers.map((l, i) => (
                    <Lever key={l.signal || i} lever={l} maxGain={maxGain} rank={i + 1} />
                ))}
            </ol>

            {counterfactual.caveat && (
                <p
                    className="mt-4 pt-3 border-t border-white/5 text-[11px] leading-snug text-text-secondary/55"
                    data-testid="lever-caveat"
                >
                    {counterfactual.caveat}
                </p>
            )}
        </div>
    );
}
